'use client';

/**
 * DataGrid toolbar — Phase A1.
 *
 * Top strip of the grid:
 *   [Title • count]      [search box] [filter chips] [actions] [CSV]
 *
 * - Title + count anchor the surface so users always know what they're
 *   looking at + how many.
 * - Search is DEBOUNCED at 250ms. The grid hands the debounced value
 *   to the consumer's filter pipeline; we never mutate data ourselves.
 * - Filter chips render one per active filter. Each chip carries an
 *   X to clear that single dimension; a "Clear all" appears when any
 *   filter is active.
 * - The actions slot is a render-prop for the consumer (typically
 *   "+ New …" buttons, refresh, etc).
 * - CSV button surfaces only when `onExportCsv` is wired.
 *
 * All interactions are keyboard-accessible. Focus rings use the brand
 * `statura-green-accessible` token to match the rest of the UI.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Search, X } from 'lucide-react';
import { Input } from '../input';
import { Button } from '../button';
import { MoreControls } from '../more-controls';
import { cn } from '../../lib/utils';
import type { ActiveFilters, FilterSpec } from './types';
import { labelForOption } from './filter-options';

export interface DataGridToolbarProps {
  compactControls?: boolean;
  moreControls?: React.ReactNode;
  primaryControls?: React.ReactNode;
  title?: string;
  /** Count of the rows supplied to this grid. */
  totalCount?: number;
  /** Page-scoped count when the grid receives a server page. */
  countLabel?: string;
  /** Initial value for the search box (controlled-by-default for tests). */
  searchValue?: string;
  /** Fired on debounced search changes (250 ms). Empty string = cleared. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Filter dimensions — drives chip rendering. */
  filters?: ReadonlyArray<FilterSpec>;
  /** Active values keyed by `FilterSpec.id`. */
  activeFilters?: ActiveFilters;
  /** Consumer wires this to mutate / reset filter values. */
  onFilterClear?: (filterId: string) => void;
  /** Optional "Clear all" handler. If omitted, button is hidden. */
  onClearAllFilters?: () => void;
  /** Render-prop slot for action buttons (right side). */
  actions?: React.ReactNode;
  /** Show + wire the CSV export button. */
  onExportCsv?: () => void;
  /** Hide the search box (some grids are filter-only). */
  hideSearch?: boolean;
  /**
   * Phase F1 Sprint 1 — filter SETTER widgets rendered below the search
   * row when present. Distinct from the chips display row: chips show
   * what's ACTIVE; setters let the user CHANGE the values.
   */
  filterControlsSlot?: React.ReactNode;
  /**
   * Phase F1 Sprint 1 — column / density controls slot. Typically the
   * `<DataGridColumnControls />` widget rendered at the right side of
   * the actions row.
   */
  columnControlsSlot?: React.ReactNode;
}

export function DataGridToolbar({
  title,
  compactControls = false,
  moreControls,
  primaryControls,
  totalCount,
  countLabel,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search…',
  filters,
  activeFilters,
  onFilterClear,
  onClearAllFilters,
  filterControlsSlot,
  columnControlsSlot,
  actions,
  onExportCsv,
  hideSearch,
}: DataGridToolbarProps) {
  const { localSearch, handleSearchChange, clearSearch } = useDebouncedGridSearch(
    searchValue,
    onSearchChange,
  );
  const chips = getActiveFilterChips(filters, activeFilters);
  const hasActiveFilters = chips.length > 0 || (!!localSearch && localSearch.trim().length > 0);

  // Phase F4 S8 — suppress the toolbar shell entirely when nothing
  // would render in it. Otherwise FocusedList consumers that don't
  // opt into v0.2 controls see an empty bordered strip above the
  // table.
  // totalCount alone is NOT counted as content — it only renders inside
  // the title block, so without `title` there's nothing to show.
  const hasAnyToolbarContent =
    (compactControls && typeof totalCount === 'number') ||
    !!moreControls ||
    !!primaryControls ||
    !!title ||
    !!actions ||
    !!onExportCsv ||
    !!filterControlsSlot ||
    !!columnControlsSlot ||
    (!hideSearch && !!onSearchChange) ||
    (filters && filters.length > 0) ||
    hasActiveFilters;
  if (!hasAnyToolbarContent && totalCount === undefined) return null;

  return (
    <div
      className="experience-candidate-data-grid flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-3"
      data-data-grid-toolbar
    >
      {/* Top row: title + count + actions */}
      <div className="flex flex-wrap items-center gap-3">
        {title && (
          <div className="flex min-w-0 flex-1 basis-full flex-wrap items-baseline gap-2 sm:basis-auto">
            <h2 className="min-w-0 break-words font-body text-section-title font-semibold text-text-heading">
              {title}
            </h2>
          </div>
        )}

        {!compactControls ? primaryControls : null}
        {!hideSearch && onSearchChange && (
          <div
            className={cn(
              'relative flex w-full min-w-[12rem] items-center sm:ml-auto sm:max-w-sm sm:flex-1',
              !compactControls && 'order-3 sm:order-none',
            )}
          >
            <Search
              className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-text-muted"
              aria-hidden="true"
            />
            <Input
              size="toolbar"
              type="search"
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="pl-8 pr-3"
            />
            {/* Result count announced politely as the worker types. Without it a
                screen-reader user filters a list and hears nothing — the row
                count changes silently. `totalCount` is the post-filter length,
                because DataGrid passes the already-filtered `data`. Added when
                /me/courses (which had its own announced search box) was retired
                into /me/training, so the capability moved rather than being lost. */}
            {typeof totalCount === 'number' && (
              <span aria-live="polite" aria-atomic="true" className="sr-only">
                {countLabel ??
                  (localSearch
                    ? `${totalCount} ${totalCount === 1 ? 'result' : 'results'} for ${localSearch}`
                    : `${totalCount} ${totalCount === 1 ? 'result' : 'results'}`)}
              </span>
            )}
          </div>
        )}

        {compactControls ? primaryControls : null}
        <div
          className={cn(
            'flex w-full min-w-0 flex-wrap items-center gap-1.5 sm:w-auto [overflow-wrap:normal] [&>*]:max-w-full',
            !title && 'sm:ml-auto',
          )}
        >
          {actions}
          {!compactControls ? columnControlsSlot : null}
          {(compactControls && (columnControlsSlot || moreControls || onExportCsv)) ||
          moreControls ? (
            <MoreControls label="View">
              {compactControls ? columnControlsSlot : null}
              {moreControls}
              {compactControls && onExportCsv ? (
                <button type="button" onClick={onExportCsv} aria-label="Export visible rows as CSV">
                  Export CSV
                </button>
              ) : null}
            </MoreControls>
          ) : null}
          {!compactControls && onExportCsv && (
            <Button
              variant="outline"
              size="toolbar"
              type="button"
              onClick={onExportCsv}
              aria-label="Export visible rows as CSV"
            >
              <Download className="h-3 w-3" aria-hidden="true" />
              CSV
            </Button>
          )}
        </div>
        {typeof totalCount === 'number' && (
          <span
            className="ml-auto text-caption tabular-nums text-text-muted"
            aria-label={countLabel ?? `${totalCount} shown`}
          >
            {countLabel ?? `${formatCount(totalCount)} ${totalCount === 1 ? 'result' : 'results'}`}
          </span>
        )}
      </div>

      {/* Phase F1 Sprint 1 — filter setter widgets row. Rendered above
          chips so the user sets values here then sees the active chips
          summarising state below. */}
      {filterControlsSlot && (
        <div className="flex w-full flex-wrap items-center gap-1.5">{filterControlsSlot}</div>
      )}

      {/* Filter chips row — only when at least one active */}
      {hasActiveFilters && (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {chips.map((chip) =>
            // Codex Phase A pass 5 P3: render chips as non-interactive
            // labels when `onFilterClear` isn't wired. Avoids exposing
            // keyboard-focusable buttons with an X that do nothing.
            onFilterClear ? (
              <button
                key={chip.id}
                type="button"
                onClick={() => onFilterClear(chip.id)}
                aria-label={`Clear filter ${chip.label}`}
                className={cn(
                  'inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-statura-green-accessible/30 bg-statura-green-accessible/10 px-2 py-0.5 text-left text-[10.5px] font-medium text-statura-green-accessible whitespace-nowrap break-normal [overflow-wrap:normal]',
                  'hover:bg-statura-green-accessible/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
                )}
              >
                {chip.label}
                <X className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
              </button>
            ) : (
              <span
                key={chip.id}
                className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-statura-green-accessible/30 bg-statura-green-accessible/10 px-2 py-0.5 text-[10.5px] font-medium text-statura-green-accessible whitespace-normal break-words [overflow-wrap:anywhere]"
              >
                {chip.label}
              </span>
            ),
          )}
          {onClearAllFilters && (chips.length > 0 || !!localSearch.trim()) && (
            <button
              type="button"
              onClick={() => {
                // Codex Phase A pass 3 P3: Clear all must ALSO reset
                // local search state + cancel any pending debounce,
                // otherwise a typed-but-not-yet-debounced value
                // re-applies after the parent clears.
                clearSearch();
                onClearAllFilters();
              }}
              className="ml-1 text-[10.5px] font-medium text-text-muted hover:text-statura-ink focus:outline-none focus-visible:text-statura-ink"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Shared search state for integrated and register toolbar modes. */
export function useDebouncedGridSearch(searchValue = '', onSearchChange?: (value: string) => void) {
  const [localSearch, setLocalSearch] = useState(searchValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedSearchRef = useRef<string | null>(null);

  useEffect(() => {
    // A controlled acknowledgement must not overwrite a newer keystroke.
    if (searchValue === lastEmittedSearchRef.current) return;
    lastEmittedSearchRef.current = null;
    setLocalSearch(searchValue);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, [searchValue]);

  const handleSearchChange = useCallback(
    (raw: string) => {
      setLocalSearch(raw);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!onSearchChange) return;
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        lastEmittedSearchRef.current = raw;
        onSearchChange(raw);
      }, 250);
    },
    [onSearchChange],
  );

  const clearSearch = useCallback(() => {
    setLocalSearch('');
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    onSearchChange?.('');
  }, [onSearchChange]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return { localSearch, handleSearchChange, clearSearch };
}

export function getActiveFilterChips(
  filters?: ReadonlyArray<FilterSpec>,
  activeFilters?: ActiveFilters,
) {
  return (filters ?? [])
    .map((spec) => {
      const label = formatChipLabel(spec, activeFilters?.[spec.id]);
      return label ? { id: spec.id, label } : null;
    })
    .filter((chip): chip is { id: string; label: string } => chip !== null);
}

/**
 * Format an active filter into a chip label. Returns null for
 * "no active value" states so the chip is hidden.
 */
function formatChipLabel(spec: FilterSpec, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  switch (spec.kind) {
    case 'text':
      return `${spec.label}: ${String(value)}`;
    case 'select':
      return `${spec.label}: ${labelForOption(spec, value as string)}`;
    case 'multi-select': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      if (arr.length === 0) return null;
      if (arr.length === 1) return `${spec.label}: ${labelForOption(spec, arr[0]!)}`;
      return `${spec.label}: ${arr.length} selected`;
    }
    case 'boolean':
      // Codex Phase A pass 2 P2: distinguish "filter is set to false"
      // (e.g. "Inactive only") from "filter is unset". Show a chip for
      // both true + false; only return null when value is undefined/
      // null/empty (handled by the early return above).
      return value === true ? spec.label : value === false ? `${spec.label}: No` : null;
    case 'date-range': {
      const v = value as { from?: string; to?: string } | null;
      if (!v) return null;
      if (v.from && v.to) return `${spec.label}: ${v.from} → ${v.to}`;
      if (v.from) return `${spec.label}: from ${v.from}`;
      if (v.to) return `${spec.label}: to ${v.to}`;
      return null;
    }
    default:
      return null;
  }
}

function formatCount(n: number): string {
  if (n < 10000) return n.toLocaleString('en-AU');
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 100000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
